import React, { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { NewAppLayout } from '@/components/new-ux/layout/NewAppLayout'
import { Button } from "@/components/ui/button"
import { ArrowLeft, Settings } from 'lucide-react'
import { useClients } from '@/hooks/useClients'
import { ClientWizard } from '@/components/clients/ClientWizard'
import { ClientIntakeOnboarding } from '@/components/clients/ClientIntakeOnboarding'
import { PocPacingEditorModal } from '@/components/new-ux/dashboard/PocPacingEditorModal'

const ClientDetails = () => {
  const { clientId } = useParams()
  const navigate = useNavigate()
  const { data: clients = [] } = useClients()
  const [pocEditorOpen, setPocEditorOpen] = useState(false)

  const client = clients.find(c => c.id === clientId)

  if (!client && clientId) {
    return (
      <NewAppLayout>
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="outline"
            onClick={() => navigate('/clients')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Clients
          </Button>
        </div>
        <div className="text-center py-12">
          <h2 className="text-xl font-semibold mb-2">Client not found</h2>
          <p className="text-muted-foreground">The client you're looking for doesn't exist.</p>
        </div>
      </NewAppLayout>
    )
  }

  return (
    <NewAppLayout>
      <div className="flex items-center gap-4 mb-6">
        <Button
          variant="outline"
          onClick={() => navigate('/clients')}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Clients
        </Button>
      </div>

      {client && (
        <div className="w-full">
          {/* POC Pacing settings — same modal as the Dashboard ⚙ icon.
              Mounted here so admins can configure POC start date,
              length, and per-metric M1/M2/M3 targets directly from the
              client's settings page (Jamie's spec: data should be
              "synced between both pages"). */}
          <div className="mb-6 flex items-center justify-between rounded-xl border bg-white px-4 py-3 shadow-sm">
            <div>
              <div className="text-sm font-semibold text-foreground">POC Pacing</div>
              <div className="text-xs text-muted-foreground">
                Set the POC window and per-metric monthly targets for this client.
              </div>
            </div>
            <Button
              variant="outline"
              onClick={() => setPocEditorOpen(true)}
              className="flex items-center gap-2"
            >
              <Settings className="h-4 w-4" />
              Edit POC Pacing
            </Button>
          </div>

          {/* Intake-first onboarding: the client-answered half of the old
              8-step wizard now arrives via the self-hosted intake form.
              Link the client's submission here and apply it, then use the
              wizard below only for the internal-only configuration. */}
          <div className="mb-6">
            <ClientIntakeOnboarding client={client} />
          </div>

          <ClientWizard
            client={client}
            onComplete={(updatedClient) => {
              // Navigate back to clients list or refresh
              navigate('/clients')
            }}
            onCancel={() => {
              navigate('/clients')
            }}
          />
        </div>
      )}

      <PocPacingEditorModal
        open={pocEditorOpen}
        onClose={() => setPocEditorOpen(false)}
        clientId={client?.id ?? null}
        clientName={client?.name ?? null}
      />
    </NewAppLayout>
  )
}

export default ClientDetails
